import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api.ts';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import './Login.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await api.post('/api/v1/auth/forgot-password', { email });
      
      // Em desenvolvimento, o token é retornado na resposta
      // Em produção, seria enviado por email
      if (response.data.token) {
        setResetToken(response.data.token);
        setSuccess('Token de recuperação gerado! Em produção, este seria enviado por email.');
      } else {
        setSuccess(response.data.message || 'Se uma conta com esse email existir, um link de recuperação foi enviado.');
      }
    } catch (err: any) {
      console.error('Erro ao solicitar recuperação de senha:', err);
      setError(err.response?.data?.error || 'Erro ao solicitar recuperação de senha');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (resetToken) {
      navigator.clipboard.writeText(resetToken);
      alert('Token copiado! Use-o na página de reset de senha.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img 
              src="/logo.png"
              alt="Prumo" 
              className="login-logo"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target) {
                  target.style.display = 'none';
                }
              }}
            />
            <h1 className="logo-text">Prumo</h1>
          </div>
          <p className="slogan">Recuperação de Senha</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {!backendAvailable && (
            <div className="error-message" role="alert" aria-live="polite">
              Conexão com o backend necessária para recuperar senha.
            </div>
          )}
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              {error}
            </div>
          )}
          
          {success && (
            <div className="success-message" role="alert" aria-live="polite">
              {success}
            </div>
          )}

          {resetToken && (
            <div className="token-container">
              <p><strong>Token de recuperação (apenas para desenvolvimento):</strong></p>
              <div className="token-display">
                <code>{resetToken}</code>
                <button 
                  type="button" 
                  onClick={handleCopyToken}
                  className="copy-token-button"
                >
                  Copiar
                </button>
              </div>
              <p className="token-hint">
                Copie este token e use na página de reset de senha.
              </p>
              <Link to={`/reset-password?token=${resetToken}`} className="link">
                Ir para reset de senha →
              </Link>
            </div>
          )}

          {!success && (
            <>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Digite seu email"
                  disabled={loading || !backendAvailable}
                />
              </div>

              <button 
                type="submit" 
                className="login-button"
                disabled={loading || !backendAvailable}
              >
                {loading ? 'Enviando...' : 'Enviar Link de Recuperação'}
              </button>
            </>
          )}
        </form>

        <div className="login-footer">
          <p>
            Lembrou sua senha?{' '}
            <Link to="/login" className="link">
              Voltar para login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

